/**
 * ProductosModal — maestro de productos Finnegans para el módulo de obras.
 *
 * Se importa desde el export de Finnegans "Importacion de BSProducto*.xlsx"
 * (columnas Nombre, Codigo, Activo, PrecioBaseVenta). Lo usa el generador del
 * archivo de importación de pedidos para resolver los códigos del OP.
 */

import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { importarProductosObras, getProductosResumen } from "../services/obrasFS";

function toDate(ts) {
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return null;
}

function parseExcelProductos(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
  if (rows.length < 2) return [];

  const headers = rows[0].map(h => String(h || "").toLowerCase().trim());
  const iNombre = headers.findIndex(h => h === "nombre");
  const iCodigo = headers.findIndex(h => h === "codigo");
  const iActivo = headers.findIndex(h => h === "activo");
  const iPrecio = headers.findIndex(h => h === "preciobaseventa");
  if (iNombre < 0 || iCodigo < 0) {
    throw new Error('El archivo no tiene las columnas esperadas ("Nombre" y "Codigo") — usá el export de BSProducto');
  }

  return rows.slice(1)
    .map(r => ({
      codigo: String(r[iCodigo] ?? "").trim(),
      nombre: String(r[iNombre] ?? "").trim(),
      activo: iActivo >= 0 ? String(r[iActivo]) === "1" : true,
      precio: iPrecio >= 0 ? Number(r[iPrecio]) : null,
    }))
    .filter(f => f.codigo && f.nombre);
}

export default function ProductosModal({ user, onClose }) {
  const [resumen, setResumen] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    getProductosResumen().then(setResumen).catch(() => setResumen(null));
  }, []);

  async function handleArchivo(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setMsg(null);
    try {
      const filas = parseExcelProductos(await file.arrayBuffer());
      if (filas.length === 0) throw new Error("No se encontraron productos en el archivo");
      const { importados } = await importarProductosObras(filas, user, file.name);
      setMsg({ ok: true, texto: `✅ Se importaron ${importados} productos del maestro` });
      setResumen(await getProductosResumen());
    } catch (err) {
      setMsg({ ok: false, texto: "No se pudo importar: " + (err.message || err) });
    } finally {
      setBusy(false);
    }
  }

  const fecha = toDate(resumen?.actualizadoAt);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 270, padding: 16,
        overflowY: "auto",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, padding: 20, width: "100%", maxWidth: 460, margin: "auto" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>📦 Maestro de productos</h2>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>

        <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px" }}>
          Códigos y nombres de los productos de Finnegans — lo usa el generador
          del archivo de importación de pedidos. Se carga desde el export
          <b> "Importacion de BSProducto"</b> (.xlsx). Reimportalo cuando se
          agreguen productos nuevos.
        </p>

        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 12 }}>
          {resumen === undefined ? "Cargando…"
            : resumen === null ? "Todavía no se importó el maestro."
            : <>
                <b>{resumen.cantidad}</b> productos cargados
                {fecha ? ` · última importación ${fecha.toLocaleDateString("es-UY")} ` : " "}
                {resumen.porNombre ? `por ${resumen.porNombre}` : ""}
                {resumen.archivo ? <div style={{ color: "#94a3b8", fontSize: 12 }}>{resumen.archivo}</div> : null}
              </>}
        </div>

        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleArchivo} />
        <button className="btn" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? "Importando…" : "📥 Importar maestro de productos"}
        </button>

        {msg && (
          <div style={{
            marginTop: 10, fontSize: 13, padding: "8px 10px", borderRadius: 8,
            background: msg.ok ? "#f0fdf4" : "#fee2e2",
            color: msg.ok ? "#15803d" : "#b91c1c",
          }}>
            {msg.texto}
          </div>
        )}
      </div>
    </div>
  );
}
